import _ from 'lodash';
import React, { useEffect } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import HardwareDetails from '@/components/fitting-hardware/HardwareDetails';
import HardwareSelector from '@/components/fitting-hardware/HardwareSelector';
import { actions, getAccessType, getHardwareSelectionEnabled } from '@/redux/fitting-hardware';
import ApiService from '@/services';
import {
  HardwareRentPageValueType,
  HardwareOptionType,
  ReduxStateType,
  TargetGroup,
} from '@/types/fitting-hardware';
import { withRenderedCmsContent } from '@/utils/content';
import HardwareSelection from '@/components/fitting-hardware/HardwareSelection';

export interface FittingHardwareProps {
  serviceUrl: string
  tariffOfferName: string
  targetGroup: TargetGroup
  shopContext: string
  dispatchEvent: (event: CustomEvent) => void,
  selectedConnectionType: string | null
  isFmsTariff: boolean
}

export interface StateProps {
  hardwareSelectionEnabled: boolean,
  accessType: string
}

export interface DispatchProps {
  setHardwareOptions: (hardwareOptions: HardwareOptionType[]) => void
  setHardwareSelectionHint: (hardwareSelectionHint: string) => void
  setSelectedHardwareOption: (index: number) => void
  setNewSelectedHardwareOption: (index: number) => void
  removeSelectedHardwareOption: (index: number) => void
  setPageValue: (pageValue: HardwareRentPageValueType) => void
  setHardwareSelectionEnabled: (hardwareSelectionEnabled: boolean) => void
  setAccessType: (accessType: string) => void
}

const FittingHardware = ({
  serviceUrl,
  tariffOfferName,
  targetGroup,
  shopContext,
  setHardwareOptions,
  setHardwareSelectionHint,
  setSelectedHardwareOption,
  removeSelectedHardwareOption,
  setHardwareSelectionEnabled,
  setNewSelectedHardwareOption,
  setPageValue,
  dispatchEvent,
  selectedConnectionType,
  isFmsTariff,
  hardwareSelectionEnabled,
  setAccessType,
}: FittingHardwareProps & StateProps & DispatchProps) => {
  const apiService = new ApiService(serviceUrl);

  const selectHardwareOption = (index: number, hardwareOption: HardwareOptionType) => {
    apiService.selectHardwareOption({
      tariffOfferName,
      shopContext,
      articleId: hardwareOption.articleId ?? '',
      selected: hardwareOption.selected,
      ownHardware: hardwareOption.ownHardware,
    })
      .then((statusCode) => {
        if (statusCode === 200) {
          if (hardwareSelectionEnabled && hardwareOption.selected) {
            removeSelectedHardwareOption(index);
            dispatchEvent(new CustomEvent('hardwareselected', { detail: undefined }));
          } else {
            hardwareSelectionEnabled ? setNewSelectedHardwareOption(index) : setSelectedHardwareOption(index);
            dispatchEvent(new CustomEvent('hardwareselected', { detail: hardwareOption }));
          }
          dispatchEvent(new CustomEvent('hardwareSelectionButtonClicked', { detail: hardwareOption }));
        }
      });
  };
  
  useEffect(() => {
    apiService.getPageValue()
      .then((pageValue) => {
        if (pageValue) {
          setPageValue(pageValue);
        }
      });

    apiService.getHardwareOptions(tariffOfferName, targetGroup, shopContext, selectedConnectionType)
      .then((hardwareResponse) => {
        const renderedHardwareResponse = withRenderedCmsContent(hardwareResponse);
        const uniqueHardwareOptions = _.uniqBy(renderedHardwareResponse.hardware, 'articleId');
        setHardwareOptions(uniqueHardwareOptions);
        setHardwareSelectionHint(renderedHardwareResponse.hardwareSelectionHint.rendered);
        setHardwareSelectionEnabled(renderedHardwareResponse.hardwareSelectionEnabled);
        setAccessType(renderedHardwareResponse.accessType);
        const selectedHardwareOption = uniqueHardwareOptions.find((hw) => hw.selected);
        if (!!selectedHardwareOption) {
          dispatchEvent(new CustomEvent('hardwareselected', { detail: selectedHardwareOption }));
        } else {
          dispatchEvent(new CustomEvent('hardwareselected', { detail: undefined }));
        }
      });
  }, [tariffOfferName, selectedConnectionType, isFmsTariff]);

  
  const selectHardwareButtonClick = ( index: number, hardwareOption: HardwareOptionType) => {
    selectHardwareOption(index, hardwareOption);
  };

  const emitBuyHardwareEvent = () => {
    dispatchEvent(new CustomEvent('buyhardware'));
  };
  return (
    <div className="shop-tile" style={{ padding: '0px' }}>
      {hardwareSelectionEnabled ?
          <HardwareSelection
              onHardwareOptionSelected={selectHardwareButtonClick}
              isFmsTariff={isFmsTariff}
              onBuyHardwareTeaserClick={emitBuyHardwareEvent}
          /> :
          <>
              <HardwareDetails />
              <HardwareSelector
                  onBuyHardwareTeaserClick={emitBuyHardwareEvent}
                  onHardwareOptionSelected={selectHardwareOption}
                  isFmsTariff={isFmsTariff}
              />
          </>
      }

    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapStateToProps = (state: ReduxStateType): StateProps => ({
  hardwareSelectionEnabled: getHardwareSelectionEnabled(state),
  accessType: getAccessType(state),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  setHardwareOptions:
    (hardwareOptions:HardwareOptionType[]) => dispatch(actions.setHardwareOptions(hardwareOptions)),
  setHardwareSelectionHint:
    (hardwareSelectionHint:string) => (
      dispatch(actions.setHardwareSelectionHint(hardwareSelectionHint))
    ),
  setSelectedHardwareOption:
    (index:number) => (
      dispatch(actions.setSelectedHardwareOption(index))
    ),
  setNewSelectedHardwareOption:
      (index:number) => (
        dispatch(actions.setNewSelectedHardwareOption(index))
      ),
  removeSelectedHardwareOption:
      (index:number) => (
        dispatch(actions.removeSelectedHardwareOption(index))
      ),
  setPageValue: (pageValue: HardwareRentPageValueType) => dispatch(actions.setPageValue(pageValue)),
  setHardwareSelectionEnabled: (hardwareSelectionEnabled: boolean) => dispatch(actions.setHardwareSelectionEnabled(hardwareSelectionEnabled)),
  setAccessType: (accessType: string) => dispatch(actions.setAccessType(accessType)),
});

export default connect<StateProps, DispatchProps, FittingHardwareProps, ReduxStateType>(
  mapStateToProps,
  mapDispatchToProps,
)(FittingHardware);
